export default class ViewportManager {
    constructor(opts = {}) {
        this.width = window.innerWidth;
        this.height = window.innerHeight;
        this.viewportRatio = 1;
        this.pixelRatio = window.devicePixelRatio || 1;
        this.visible = !document.hidden;

        this._callbacks = [];
        this._debounceDelay = opts.debounceDelay ?? 500;
        this._rullerEl = this._createRullerEl();
        this._debouncedUpdate = this._debounce(() => this._update(), this._debounceDelay);

        this._measureScrollbarWidth();
        this._setupListeners();
        this._update();
        this._updateVisibility();
    }


    onResize(fn) {
        this._callbacks.push(fn);
        return () => {
            const i = this._callbacks.indexOf(fn);
            if (~i) this._callbacks.splice(i, 1);
        };
    }


    _setupListeners() {
        let basicResizeUsed = false;
        let resizeTimeout = null;
        const resetFlag = () => { basicResizeUsed = false; };

        window.addEventListener('resize', () => {
            basicResizeUsed = true;
            clearTimeout(resizeTimeout);
            resizeTimeout = setTimeout(resetFlag, this._debounceDelay * 0.2);
            this._onResize();
        }, false);

        if (window.visualViewport) {
            window.visualViewport.addEventListener('resize', () => {
                if (basicResizeUsed) return;
                this._onResize();
            }, false);
        }

        document.addEventListener('visibilitychange', () => this._updateVisibility(), false);
    }


    _onResize() {
        this._update();
        this._debouncedUpdate();
    }


    _update() {
        this.width = window.innerWidth;
        this.height = this._measureViewportHeight();
        this.viewportRatio = this.width / this.height;
        this._updateDpr();
        this._emit();
    }


    _updateDpr() {
        const newDpr = window.devicePixelRatio || 1;
        if (newDpr === this.pixelRatio) return;
        this.pixelRatio = newDpr;
        this._update();
        this._debouncedUpdate();
    }


    _updateVisibility() {
        this.visible = !document.hidden;
        this._update();
    }


    _emit() {
        for (let i = 0; i < this._callbacks.length; i++) this._callbacks[i](this);
    }


    _createRullerEl() {
        const el = document.createElement('div');
        Object.assign(el.style, {
            position: 'fixed',
            top: 0,
            left: 0,
            width: '200px',
            height: '10px',
            overflowY: 'scroll',
            pointerEvents: 'none',
            userSelect: 'none',
            zIndex: -1,
            opacity: 0
        });
        return el;
    }


    _measureScrollbarWidth() {
        const root = document.documentElement;
        const el = this._rullerEl;
        const child = document.createElement('div');
        Object.assign(el.style, { width: '200px', height: '10px', overflowY: 'scroll' });
        Object.assign(child.style, { width: '100%', height: '150%' });
        el.appendChild(child);
        document.body.appendChild(el);
        const zoom = parseFloat(getComputedStyle(root).zoom);
        const demult = isNaN(zoom) ? 1 : zoom;
        const scrollbarWidth = Math.round((200 - el.clientWidth) * demult);
        root.style.setProperty('--scrollbar-width', scrollbarWidth + 'px');
        document.body.removeChild(el);
        el.removeChild(child);
        Object.assign(el.style, { width: '1px', height: '100%', overflowY: 'hidden' });
        return scrollbarWidth;
    }


    _measureViewportHeight() {
        const root = document.documentElement;
        const el = this._rullerEl;
        document.body.appendChild(el);
        const height = el.getBoundingClientRect().height;
        document.body.removeChild(el);
        root.style.setProperty('--inner-height', window.innerHeight + 'px');
        root.style.setProperty('--vp-height', height + 'px');
        return height;
    }


    _debounce(fn, delay) {
        let timer;
        return (...args) => { clearTimeout(timer); timer = setTimeout(() => fn(...args), delay); };
    }
}
